import React from "react";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { FEATURE_FLAG_NAMES } from "@/core/feature-flags/feature-flag-name";
import { useFeatureFlag } from "@/core/feature-flags/use-feature-flag";
import { getUserFullName } from "@/core/utils/name";
import { cn } from "@/core/utils/utils";
import { LEGEND_COLORS } from "@/dashboard/constants";
import { DetailedUserWithStatsAndJobCount } from "@/user/user";

interface ParticipationTrackerListItemProps {
  user: DetailedUserWithStatsAndJobCount;
  maxValue: number;
}

export const ParticipationTrackerListItem = React.memo(
  ({ user, maxValue }: ParticipationTrackerListItemProps) => {
    const { enabled: showCompletedJobsOnDashboard } = useFeatureFlag(
      FEATURE_FLAG_NAMES.show_completed_jobs_on_dashboard,
    );

    const signupCount = user.stats?.signup_count ?? 0;
    const jobsCompleted = user.job_count ?? 0;

    const signupWidthPercentage =
      maxValue > 0 ? (signupCount / maxValue) * 100 : 0;
    const jobsWidthPercentage =
      maxValue > 0 ? (jobsCompleted / maxValue) * 100 : 0;

    // Determine if signups bar should have right rounding
    const isSignupsWider = signupWidthPercentage > jobsWidthPercentage;

    return (
      <Stack gap="xs" fullWidth>
        <Typography variant="subtitleW3">{getUserFullName(user)}</Typography>
        <Stack direction="row" align="center" justify="between" fullWidth>
          <Stack direction="row" align="center" fullWidth className="relative">
            {/* Jobs completed bar (background) - always behind */}
            <div
              className={cn("h-3 rounded-md", LEGEND_COLORS.jobs_completed)}
              style={{
                width: `${jobsWidthPercentage}%`,
              }}
            />
            {/* Signups bar (foreground) - always on top */}
            <div
              className={cn(
                "absolute h-3",
                isSignupsWider ? "rounded-md" : "rounded-l-md",
                LEGEND_COLORS.signups,
              )}
              style={{
                width: `${signupWidthPercentage}%`,
              }}
            />
          </Stack>
          <Stack direction="row" gap="sm" className="ml-4 flex-shrink-0">
            <Typography variant="bodyW2">{signupCount}</Typography>
            {showCompletedJobsOnDashboard && (
              <>
                <Typography variant="bodyW2" textColor="tertiary">
                  /
                </Typography>
                <Typography variant="bodyW2">{jobsCompleted}</Typography>
              </>
            )}
          </Stack>
        </Stack>
      </Stack>
    );
  },
);
